import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import { useQuery } from '@tanstack/react-query';
import projectKeys from './keyFactory';

export const solarDesignerStateKey = (projectId: string, designId?: string) =>
  [
    ...projectKeys.project(projectId),
    'solarDesignerState',
    ...(designId ? [designId] : []),
  ] as const;

graphql(`
  fragment SolarDesignDraftFields on SolarDesignDraft {
    id
    name
    description
    revision
    basis
    sourceAnalysisId
    sourceAnalysisVersion
    state
    updatedAt
    isStale
    staleReason
    sourceSite {
      addressId
      coordinates {
        latitude
        longitude
      }
    }
    isUsedByEstimate
    analysis {
      ...SolarEstimateSummaryFields
    }
    recommendedPanelAppliances {
      ...SolarRecommendedApplianceFields
    }
    recommendedBatteryAppliances {
      ...SolarRecommendedApplianceFields
    }
  }
`);

graphql(`
  fragment SolarGeometryGenerationFields on SolarGeometryGeneration {
    recoveryReason
    id
    status
    error
    updatedAt
    result {
      ...SolarEstimateSummaryFields
    }
  }
`);

export const SolarDesignerStateQuery = graphql(`
  query SolarDesignerState($projectId: ID!, $designId: ID) {
    projectById(id: $projectId) {
      publicId
      solarDesignDraft(designId: $designId) {
        ...SolarDesignDraftFields
      }
      solarGeometryGeneration {
        ...SolarGeometryGenerationFields
      }
      addresses {
        site {
          postalCode
          propertyInformation {
            electricityOccupancyArchetype
            annualElectricityConsumptionKwh
          }
        }
      }
    }
  }
`);

export const useSolarDesignerState = (
  projectId: string,
  enabled = true,
  designId?: string
) =>
  useQuery({
    queryKey: solarDesignerStateKey(projectId, designId),
    queryFn: () =>
      gqlClient.request(SolarDesignerStateQuery, { projectId, designId }),
    enabled,
    // Roof analysis runs as a background job on the server with no push
    // channel, so finishing can only be observed by asking. Nothing else here
    // changes on its own, so the poll stops once the job leaves the queue.
    refetchInterval: (query) => {
      const status =
        query.state.data?.projectById.solarGeometryGeneration?.status;
      return status === 'QUEUED' || status === 'RUNNING' ? 2_000 : false;
    },
    refetchIntervalInBackground: false,
  });
